import Cookie from 'js-cookie';

import {
  Config,
  LogEntriesConstantsEnums,
  LogEntryPriorityEnum,
  LogEntryTypesEnums
} from '@nw/shared/config';
import {
  captureLogEntriesExceptionWithScope,
  isGammaOrProdEnv
} from '@nw/shared/utils';

import { clearAllFormsDomainTokens } from '../FormsStorageUtils';
import { clearAllCollegePredictorDomainTokens } from '../NwCollegePredictorStorageUtils';
import { clearAllPaymentsDomainTokens } from '../PaymentStorageUtils';

// Note: Using environment-specific keys because cookies are domain-specific.
// If different environments (like beta, gamma) share the same top-level domain (e.g., .earlywave.in),
// using the same cookie keys would cause conflicts. Distinct keys ensure proper isolation.
const ACCOUNTS_ACCESS_TOKEN_KEY = isGammaOrProdEnv()
  ? 'FktanBbpTU'
  : 'RGJfeOwMsH';
const ACCOUNTS_REFRESH_TOKEN_KEY = isGammaOrProdEnv()
  ? 'ALZzOatVxQ'
  : 'LKfcUeSxkZ';
export const FALL_BACK = isGammaOrProdEnv() ? 'BdRFSpcHFi' : 'fHXCEojLpA';
const INCOGNITO_COOKIE = isGammaOrProdEnv() ? 'KhryuS3bST' : 'LynxeksocP';
const USER_ID_COOKIE = isGammaOrProdEnv() ? 'uPruEGemmc' : 'uKvsMxQbn';

interface BaseCookieOptionTypes {
  expires: number;
  path: string;
  domain?: string;
  secure?: boolean;
}

export function getCookie(key: string) {
  if (Cookie.get(key) !== undefined) return Cookie.get(key);
  else return Cookie.get(`${key}_${FALL_BACK}`);
}

const getCookieDomainBasedOnEnvironment = () => {
  const appCookieDomain = Config['APP_AUTH_COOKIE_DOMAIN'];
  return appCookieDomain;
};

export function getAppCookieDomain(
  providedDomain?: string
): string | undefined {
  const isEmbedForms = Config['APP_NAME'] === 'embed-forms';
  const isNiatApplication = Config['APP_NAME'] === 'niat-application-portal';
  const currentDomain = window.location.hostname;

  if (isNiatApplication) {
    return getCookieDomainBasedOnEnvironment();
  }

  return providedDomain;
}

export function getAppCookieDomainToClearTokens() {
  const cookieDomain = getAppCookieDomain();
  if (cookieDomain) {
    return cookieDomain;
  } else {
    return getCookieDomainBasedOnEnvironment();
  }
}

export function setCookie(
  key: string,
  value: string,
  expiryInDays: any = 30,
  domain = undefined
) {
  const isDevelopment =
    process.env['NODE_ENV'] === 'development' ||
    window.location.hostname === 'localhost';

  const baseCookieOptions: BaseCookieOptionTypes = {
    expires: expiryInDays,
    path: '/',
    ...(isDevelopment ? {} : { secure: true })
  };

  if (isDevelopment) {
    Cookie.set(key, value, baseCookieOptions);
    return;
  }

  const cookieDomain = getAppCookieDomain(domain);

  Cookie.set(key, value, {
    ...baseCookieOptions,
    domain: cookieDomain,
    sameSite: 'None'
  });
  Cookie.set(`${key}_${FALL_BACK}`, value, {
    ...baseCookieOptions,
    domain: cookieDomain
  });
}

export function getAccessToken() {
  return getCookie(ACCOUNTS_ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return getCookie(ACCOUNTS_REFRESH_TOKEN_KEY);
}

export const getCookieAttrubutes = () => {
  let neededAttributes: Record<string, any> = {
    path: '/'
  };
  if (
    process.env['NODE_ENV'] !== 'development' &&
    window.location.hostname !== 'localhost'
  ) {
    neededAttributes = { ...neededAttributes, secure: true };
  }
  return neededAttributes;
};

export const clearAccessToken = (domain?: string) => {
  Cookie.remove(ACCOUNTS_ACCESS_TOKEN_KEY, {
    ...getCookieAttrubutes(),
    domain: domain,
    sameSite: 'None'
  });
  Cookie.remove(`${ACCOUNTS_ACCESS_TOKEN_KEY}_${FALL_BACK}`, {
    ...getCookieAttrubutes(),
    domain: domain
  });
};

export const clearRefreshToken = (domain?: string) => {
  Cookie.remove(ACCOUNTS_REFRESH_TOKEN_KEY, {
    ...getCookieAttrubutes(),
    domain: domain,
    sameSite: 'None'
  });
  Cookie.remove(`${ACCOUNTS_REFRESH_TOKEN_KEY}_${FALL_BACK}`, {
    ...getCookieAttrubutes(),
    domain: domain
  });
};

export const clearAuthDomainTokens = () => {
  const appCookieDomain = getAppCookieDomainToClearTokens();
  if (appCookieDomain) {
    clearAccessToken(appCookieDomain);
    clearRefreshToken(appCookieDomain);
  }
};

export function setAccessTokenToDomain(accessToken: string, expiryInDays = 30) {
  setCookie(ACCOUNTS_ACCESS_TOKEN_KEY, accessToken, expiryInDays);
}

export function setRefreshTokenToDomain(
  refereshToken: string,
  expiryInDays = 30
) {
  setCookie(ACCOUNTS_REFRESH_TOKEN_KEY, refereshToken, expiryInDays);
}

export function clearUserSession() {
  const isDevelopment =
    process.env['NODE_ENV'] === 'development' ||
    window.location.hostname === 'localhost';

  const baseAttributes = {
    path: '/',
    ...(isDevelopment ? {} : { secure: true })
  };

  const cookies = Object.keys(Cookie.get());
  const userId = getUserId();

  cookies.forEach((cookieName) => {
    if (
      cookieName !== INCOGNITO_COOKIE &&
      cookieName !== `${INCOGNITO_COOKIE}_${FALL_BACK}`
    ) {
      const appCookieDomain = getAppCookieDomainToClearTokens();

      Cookie.remove(cookieName, baseAttributes);
      if (appCookieDomain) {
        Cookie.remove(cookieName, {
          ...baseAttributes,
          domain: appCookieDomain,
          sameSite: 'None'
        });
      }

      Cookie.remove(`${cookieName}_${FALL_BACK}`, baseAttributes);
      if (appCookieDomain) {
        Cookie.remove(`${cookieName}_${FALL_BACK}`, {
          ...baseAttributes,
          domain: appCookieDomain
        });
      }
    }
  });

  captureLogEntriesExceptionWithScope(
    LogEntriesConstantsEnums.CLEAR_USER_SESSION,
    {
      path: window.location.href,
      cookiesAfterClearing: Cookie.get(),
      cookiesAfterClearingCount: Object.keys(Cookie.get()).length,
      userId
    },
    LogEntryTypesEnums.INFORMATION,
    LogEntryPriorityEnum.MEDIUM
  );
}

export function getUserId() {
  return getCookie(USER_ID_COOKIE);
}

export function setUserId(userId: string, expiryInDays = 30) {
  setCookie(USER_ID_COOKIE, userId, expiryInDays);
}

const clearAllAccountsDomainTokens = () => {
  clearAuthDomainTokens();
  clearRefreshToken();
  clearAccessToken();
};

export const clearAllClientDomainTokens = () => {
  clearAllAccountsDomainTokens();
  clearAllPaymentsDomainTokens();
  clearAllFormsDomainTokens();
  clearAllCollegePredictorDomainTokens();
};
